import {
  AGENT_STANDARD_PLAN,
  BROKER_SEAT_TIERS,
  ENTERPRISE_PLAN as ENTERPRISE_CATALOG,
} from "@billing/catalog.js";

function localAppOrigin() {
  if (typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) {
    return "http://localhost:5173";
  }
  return null;
}

export const APP_ORIGIN =
  localAppOrigin() || import.meta.env.VITE_APP_ORIGIN || "https://rechecklist.pro";

export function appUrl(path = "/") {
  const origin = String(APP_ORIGIN).replace(/\/+$/, "");
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${origin}${suffix}`;
}

export const REGISTER_URL = appUrl("/register");
export const APP_HOME_URL = appUrl("/");
export const AGENT_LOGIN_URL = appUrl("/agent/login");
export const BROKER_LOGIN_URL = appUrl("/broker/login");

/** Deep link that focuses/opens the installed REChecklist Windows desktop app. */
export const WINDOWS_APP_URL = "rechecklist://open";

export function deskUrl(role) {
  if (role === "broker" || role === "corporate") return appUrl("/broker/dashboard");
  if (role === "agent") return appUrl("/agent/dashboard");
  if (role === "admin") return appUrl("/admin");
  if (role === "seller") return appUrl("/seller/home");
  if (role === "buyer") return appUrl("/start");
  return APP_HOME_URL;
}

export const MEMBERSHIP_LOCK_KEY = "dh_membership_activation";

export const PLATFORM_FEATURES = [
  "Mobile-first SPA",
  "Add-to-Home-Screen",
  "Local data storage",
  "Export tools",
  "Professional UI/UX",
  "Secure account system",
  "Transparent billing",
  "Transparent cancellation",
  "No data retention after closure",
];

/** Broker Office seat tiers — membership pricing (monthly; annual ≈ 20% off). */
export const SEAT_TIERS = BROKER_SEAT_TIERS.map((row) => ({
  id: row.id,
  name: row.label,
  seats: row.label,
  monthly: row.monthly,
  annual: row.annual,
}));

export const AGENT_PLAN = {
  id: "agent",
  name: AGENT_STANDARD_PLAN.name,
  subtitle: "The Starter Spark",
  monthly: AGENT_STANDARD_PLAN.monthly,
  annual: AGENT_STANDARD_PLAN.annual,
  ctaNote: "Start Elevating Your Business",
  note: "Start strong. Deliver clarity, confidence, and a modern client experience.",
  features: [
    "Full buyer & seller journeys",
    "Client messaging & file packet",
    "Invite links & progress tracking",
    ...PLATFORM_FEATURES,
  ],
};

export const BROKER_PLAN = {
  id: "broker",
  name: "Broker Office",
  subtitle: "Team Advantage",
  ctaNote: "Modernize Your Entire Office",
  note: "Empower your office with modern tools and unified workflows.",
  features: [
    "Office-level tools",
    "Agent management",
    "Team workflows",
    "Client dashboards",
    "Invite links",
    "Progress tracking",
    ...PLATFORM_FEATURES,
  ],
};

export const MULTI_OFFICE_PLAN = {
  id: "multi-office",
  name: "Multi-Office",
  subtitle: "Multi-Office Suite",
  custom: true,
  note: "Coordinate multiple offices with unified visibility and shared standards.",
  features: ["Multi-office management", "Unified dashboards", ...PLATFORM_FEATURES],
};

export const ENTERPRISE_PLAN = {
  id: "enterprise",
  name: ENTERPRISE_CATALOG.name,
  subtitle: "Enterprise Excellence",
  custom: true,
  monthly: ENTERPRISE_CATALOG.monthly,
  annual: ENTERPRISE_CATALOG.annual,
  note: "Scale excellence across your organization with enterprise-grade workflows.",
  features: ["Enterprise workflows", "Integrations", ...PLATFORM_FEATURES],
};

export const MEMBERSHIP_PLANS = [AGENT_PLAN, BROKER_PLAN, MULTI_OFFICE_PLAN, ENTERPRISE_PLAN];

export const GUEST_PREVIEW = {
  name: "Guest Preview",
  price: "Free",
  note: "Explore the journeys visually. Fields stay locked until your agent invites you.",
};

export function readMembershipActivation() {
  try {
    const raw = localStorage.getItem(MEMBERSHIP_LOCK_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function writeMembershipActivation(payload) {
  const next = {
    ...payload,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(MEMBERSHIP_LOCK_KEY, JSON.stringify(next));
  return next;
}

export function clearMembershipActivation() {
  localStorage.removeItem(MEMBERSHIP_LOCK_KEY);
}
